import React, { Component } from "react";
import { toUpper } from "lodash";

import { defined } from "@formant/core/common/defined";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IAggregateEvent } from "@formant/core/model/IAggregateEvent";
import { IEvent } from "@formant/core/model/IEvent";
import { Fader } from "@formant/ui/component/common/Fader";
import { HStack } from "@formant/ui/component/common/HStack";

import { formatDateTime } from "../../../utils/formatDateTime";
import { DeviceName } from "../../common/DeviceName";
import { EventChip } from "../../common/EventChip";
import { EventHeadline } from "../../common/EventHeadline";
import { EventIcon } from "../../common/EventIcon";
import { Linkify } from "../../common/Linkify";
import { StreamIcon } from "../../common/StreamIcon";
import { Tags } from "../../common/Tags";
import * as styles from "./EventHover.scss";

export interface IEventHoverProps {
    event?: IEvent;
    aggregateEvent?: IAggregateEvent;
    aggregatePreviewEvents?: IEvent[];
    position?: number;
}

export class EventHover extends Component<IEventHoverProps> {
    private cachedProps?: IEventHoverProps;

    public render() {
        this.cachedProps = {
            ...this.cachedProps,
            ...this.props,
        };
        const props = this.cachedProps;

        const { event, aggregateEvent, aggregatePreviewEvents, position } =
            props;

        const visible =
            (position &&
                (this.props.event || this.props.aggregateEvent) &&
                true) ||
            false;

        const ofPhrase =
            aggregatePreviewEvents?.length !== aggregateEvent?.count
                ? ` of ${aggregateEvent?.count}`
                : "";

        return (
            <Fader
                visible={visible}
                className={styles["event-hover"]}
                style={{ left: `${position}px` }}
            >
                {aggregateEvent && aggregatePreviewEvents && (
                    <h2>
                        {aggregatePreviewEvents.length}
                        {ofPhrase} events
                    </h2>
                )}
                {event && renderEvent(event, true)}
                {aggregatePreviewEvents?.map(_ => renderEvent(_, false))}
            </Fader>
        );
    }
}

function renderEvent(event: IEvent, detail: boolean) {
    return (
        <div key={event.id} className={styles.event}>
            <HStack vAlign="center" className={styles.heading}>
                <EventChip event={event} />
                <EventIcon event={event} />
                <div className={styles.severity}>
                    <EventHeadline event={event} />
                </div>
                <div className={styles.time}>{renderEventTime(event)}</div>
            </HStack>
            {detail && (
                <div className={styles.message}>
                    <Linkify text={event.message} />
                </div>
            )}
            {detail &&
                (event.deviceId || (event.streamType && event.streamName)) && (
                    <HStack className={styles.footer}>
                        {event.deviceId && (
                            <DeviceName
                                id={event.deviceId}
                                className={styles.device}
                            />
                        )}
                        {event.streamType && event.streamName && (
                            <HStack vAlign="center" className={styles.stream}>
                                <StreamIcon type={event.streamType} />
                                <div className={styles["stream-name"]}>
                                    {event.streamName}
                                </div>
                            </HStack>
                        )}
                    </HStack>
                )}
            {detail && event.type === "stateful" && !event.endTime && (
                <div className={styles.text}>
                    Event Status:{" "}
                    <b style={{ color: severityToColor(event.severity) }}>
                        ACTIVE{" "}
                        {event.severity ? " - " + toUpper(event.severity) : ""}
                    </b>
                </div>
            )}
            {detail && event.type === "stateful" && event.endTime && (
                <div className={styles.text}>Event Status: RESOLVED</div>
            )}
            {detail && event.tags && Object.keys(event.tags).length > 0 && (
                <div className={styles.footer}>
                    <Tags tags={event.tags} singleLine={true} />
                </div>
            )}
        </div>
    );
}

// In general, display the event timestamp.
// Except for comments: use the edit timestamp if it's edited,
// else use the create timestamp.
function renderEventTime(event: IEvent) {
    let time = <>{formatDateTime(parseIsoDate(event.time))}</>;
    if (event.type === "comment") {
        const [edited, timestamp] = event.editedAt
            ? [true, defined(event.editedAt)]
            : [false, defined(event.createdAt)];
        time = (
            <>
                {formatDateTime(parseIsoDate(timestamp))}
                {edited && <span className={styles.edited}> edited</span>}
            </>
        );
    }
    return time;
}

function severityToColor(severity?: string): string {
    switch (severity) {
        case "critical":
            return "#EA719D";
        case "warning":
            return "#A961E4";
        case "info":
            return "#20A0FF";
        case "error":
            return "#F89973";
        default:
            return "gray";
    }
}
